import BrowserDetect from "../../util/BrowserDetect";
import TickUtil from "../../util/TickUtil";
import TextWrap from "./TextWrap";

/**
 * AbstractTextInput.js
 * 
 * Created on 10-okt-2014, 16:35:20
 *
 * @author Harm Boschloo for Ranj
 **/
export default class AbstractTextInput {
	constructor(target, width, height, htmlContainerId, maxCharacters = 0) {
		this._target = target;
		this._width = width;
		this._height = height;
		this._htmlContainerId = htmlContainerId;

		this._maxCharacters = maxCharacters;
		this._maxLines = 0;

		this._textChangedCallback = null;

		this._id = "textInput" + this._target.id;
		this._text = "";
		this._defaultText = this._target.text;

		this._wrapText = true;
		this._wrappedText = "";

		this._hasFocus = false;

		this._handleInputBound = this._handleInput.bind(this);
		this._handleFocusBound = this._handleFocus.bind(this);
		this._handleBlurBound = this._handleBlur.bind(this);
		this._handleClickBound = this._handleClick.bind(this);
		this._handleFocusTickBound = this._handleFocusTick.bind(this);
	}

	_init(domElement) {
		this._domElement = domElement;
		this._htmlElement = this._domElement.htmlElement;

		this._target.parent.addChild(this._domElement);
		this._domElement.visible = false;

		this.addEventListener("input", this._handleInputBound);
		this.addEventListener("propertychange", this._handleInputBound);
		this.addEventListener("focus", this._handleFocusBound);
		this.addEventListener("blur", this._handleBlurBound);

		// capture click events on the text skin
		// most of the text area is transparent so this doesn't work without a hit area
		this._target.addEventListener("click", this._handleClickBound);
		this._target.hitArea = new createjs.Shape();
		var x = this.isTextCentered() ? -this._width / 2 : 0;
		this._target.hitArea.graphics.beginFill("#F00").drawRect(x, 0, this._width, this._height);
		// debug text area border
		//var shape = new createjs.Shape();
		//shape.graphics.beginStroke("#F00").drawRect(domElement.x, domElement.y, this._width, this._height);
		//this._target.parent.addChild(shape);
	}

	destroy() {
		this.removeEventListener("input", this._handleInputBound);
		this.removeEventListener("propertychange", this._handleInputBound);
		this.removeEventListener("focus", this._handleFocusBound);
		this.removeEventListener("blur", this._handleBlurBound);
		this._target.removeEventListener("click", this._handleClickBound);

		document.getElementById(this._id).remove();
	}

	addEventListener(eventType, eventHandler) {
		this._htmlElement.addEventListener(eventType, eventHandler);
	}

	removeEventListener(eventType, eventHandler) {
		this._htmlElement.removeEventListener(eventType, eventHandler);
	}

	setText(text) {
		this._text = text;
		this._updateWrappedText();
		this._updateText();
	}

	getText() {
		return this._text;
	}

	setDefaultText(defaultText) {
		this._defaultText = defaultText;
		this._updateText();
	}

	getDefaultText() {
		return this._defaultText;
	}

	setWrapText(wrapText) {
		if (wrapText !== this._wrapText) {
			this._wrapText = wrapText;
			this._updateWrappedText();
		}
	}

	getWrapText() {
		return this._wrapText;
	}

	getWrapedText() {
		return this._wrapedText;
	}

	setMaxCharacters(maxCharacters) {
		this._maxCharacters = maxCharacters;
	}

	getMaxCharacters() {
		return this._maxCharacters;
	}

	setMaxLines(maxLines) {
		this._maxLines = maxLines;
	}

	getMaxLines() {
		return this._maxLines;
	}

	setTextChangedCallback(textChangedCallback) {
		this._textChangedCallback = textChangedCallback;
	}

	getTextChangedCallback() {
		return this._textChangedCallback;
	}

	getHtmlElement() {
		return this._htmlElement;
	}

	setEnabled(enabled) {
		this._htmlElement.disabled = !enabled;
	}

	getEnabled() {
		return !this._htmlElement.disabled;
	}

	setReadOnly(readOnly) {
		this._htmlElement.readOnly = readOnly;
	}

	isReadOnly() {
		return this._htmlElement.readOnly;
	}

	focus() {
		if (this._domElement.visible) {
			this._htmlElement.focus();
		} else {
			this._showInput();
		}
	}

	hasFocus() {
		return this._hasFocus;
	}

	blur() {
		this._htmlElement.blur();
	}

	hideInput() {
		if (this._domElement.visible) {
			this._hideInput();
		}
	}

	isTextCentered() {
		return this._target.textAlign === "center";
	}

	setVisible(visible) {
		if (visible !== this.isVisible()) {
			if (!visible) {
				this._hideInput();
			}
			this._target.visible = visible;
		}
	}

	isVisible() {
		return this._target.visible || this._domElement.visible;
	}

	_handleInput(e) {
		var ok = true;
		var newText = this._htmlElement.value;
		var newWrappedText = "";

		//		console.log("new text: " + newText);

		if (newText) {
			if (this._maxCharacters > 0 && newText.length > this._maxCharacters) {
				newText = newText.slice(0, this._maxCharacters);
				this._htmlElement.value = newText;
			}

			if (ok) {
				// TODO wrap text here, or in _hideInput, or make configurable
				// for max lines to work properly text wrapping must be done here...
				if (this._wrapText) {
					// TODO have to hack the context here
					var lines = TextWrap.wrapText(newText, this._width, this._getContext());

					// check max lines
					ok = this._maxLines <= 0 || lines.length <= this._maxLines;

					// reconstruct text
					if (ok) {
						// newWrappedText = TextWrap.combineLines(lines);
					}
				}
				else if (this._maxLines > 0) {
					// check max lines
					var lineCount = (newText.match(/(\r\n|\r|\n)/g) || []).length + 1;
					ok = lineCount <= this._maxLines;
				}
			}
		}

		if (ok && newText !== this._text) {
			this._text = newText;
			this._wrappedText = newWrappedText;

			//console.log("text       : " + this._text);
			//console.log("wraped text: " + this._wrappedText);

			if (this._textChangedCallback) {
				this._textChangedCallback(this);
			}
		} else {
			this._htmlElement.value = this._text;
		}
	}

	_getContext() {
		// TODO have to hack the context here
		return this._target._prepContext(createjs.Text._workingContext);
	}

	_handleFocus(e) {
		//console.log("onfocus");
		this._hasFocus = true;
	}

	_handleBlur(e) {
		//console.log("onblur");
		this._hideInput();
	}

	_handleClick(e) {
		//console.log("click");
		if (!this.isReadOnly()) {
			this._showInput();
		}
	}

	_handleFocusTick() {
		this.focus();
	}

	_showInput() {
		//console.log("click");
		this._target.visible = false;
		this._domElement.visible = true;
		this._updateText();
		// focus the text area
		this.focus();
		// calling focus here doesn't always work, so we call it on the next tick as well
		// in iOS8 this seems to cause the keyboard to disappear again, so don't do this in iOS
		if (!BrowserDetect.isIOS()) {
			TickUtil.callOnNextTick(this._handleFocusTick, null, this);
		}
	}

	_hideInput() {
		this._hasFocus = false;
		this._target.visible = true;
		if (this._domElement.visible) {
			this._domElement.visible = false;
			this._htmlElement.blur();
		}
		this._updateText();
	}

	_updateText() {
		if (this._target.visible) {
			if (this._text) {
				if (this._wrapText) {
					this._target.text = this._wrappedText;
				} else {
					this._target.text = this._text;
				}
			} else {
				this._target.text = this._defaultText;
			}
		}

		if (this._domElement.visible) {
			this._htmlElement.value = this._text;
		}
	}

	_updateWrappedText() {
		if (this._wrapText) {
			this._wrappedText = TextWrap.wrapText(this._text, this._width, this._getContext());
		} else {
			this._wrappedText = "";
		}
	}
}